import { GridApi } from "ag-grid-community";
import _ from "lodash";
import { useCallback, useReducer } from "react";
import { GridData } from "../../components/ag-grid-extensions/models/GridData";
import { RowDef } from "../../components/ag-grid-extensions/models/RowDef";
import { ViewOptionStateEnum } from "../../components/contextual-app-bar/ViewOptionStateEnum";
import { actionItems, GaugeSheetActionItem } from "./GaugeSheetActions";
import { sortAndDispatchActions } from "../../common/utilities/SortAndDispatchActions";

export type GaugeSheetErrorType = {
    error: boolean;
    message: string | undefined
}


export enum GaugeSheetViewMode {

    Spreadsheet = 0,
    Form = 1

}

export type GaugeSheetStateType = {
    viewMode: GaugeSheetViewMode;
    viewOptionState: ViewOptionStateEnum;
    actions: GaugeSheetActionItem[];
    date: Date,
    previousDate: Date,
    resetDate: boolean;
    data: GridData | undefined,
    totalRow: RowDef | undefined,
    showDevice: boolean;
    showTanks: boolean;
    gridApi: GridApi | undefined;
    validationError: GaugeSheetErrorType | undefined;
    downloadUid: string | undefined;
    refreshing: boolean;
};

const defaultGaugeSheetState: GaugeSheetStateType = {

    viewMode: GaugeSheetViewMode.Spreadsheet,
    viewOptionState: ViewOptionStateEnum.Default,
    actions: actionItems,
    date: new Date(),
    previousDate: new Date(),
    resetDate: false,
    data: undefined,
    totalRow: undefined,
    showDevice: true,
    showTanks: true,
    gridApi: undefined,
    validationError: undefined,
    downloadUid: undefined,
    refreshing: false
};

enum GaugeSheetStateActionType {
    ActionsChanged = 'GAUGESHEET_ACTIONS_CHANGED',
    ViewOptionStateChanged = 'GAUGESHEET_OPTIONS_STATE_CHANGED',
    DateChanged = 'GAUGESHEET_DATE_CHANGED',
    ResetDateChanged = 'GAUGESHEET_RESET_DATE_CHANGED',
    DataChanged = 'GAUGESHEET_DATA_CHANGED',
    ShowDeviceChanged = 'GAUGESHEET_SHOW_DEVICE',
    ShowTankChanged = 'GAUGESHEET_SHOW_TANK',
    GridApiChanged = 'GRID_API_CHANGED',
    ValidationErrorChanged = 'GRID_VALIDATION_ERROR_CHANGED',
    TotalRowChanged = 'GRID_TOTAL_ROW_CHANGED',
    DownloadUidChanged = 'GRID_DOWNLOAD_UID_CHANGED',
    ViewModeChanged = 'GAUGESHEET_VIEW_MODE_CHANGED',
    RefreshingChanged = 'GAUGESHEET_VIEW_REFRESHING',
    Reset = 'TAG_HISTORY_RESET'
}

type GaugeSheetStateActions = | {
    type: GaugeSheetStateActionType.ActionsChanged;
    payload: GaugeSheetActionItem[];
} | {
    type: GaugeSheetStateActionType.DateChanged;
    payload: Date;
} | {
    type: GaugeSheetStateActionType.ResetDateChanged;
    payload: boolean;
} | {
    type: GaugeSheetStateActionType.DataChanged;
    payload: GridData;
} | {
    type: GaugeSheetStateActionType.ShowDeviceChanged;
    payload: boolean;
} | {
    type: GaugeSheetStateActionType.ShowTankChanged;
    payload: boolean;
} | {
    type: GaugeSheetStateActionType.GridApiChanged;
    payload: GridApi | undefined;
} | {
    type: GaugeSheetStateActionType.TotalRowChanged;
    payload: RowDef;
} | {
    type: GaugeSheetStateActionType.ValidationErrorChanged;
    payload: GaugeSheetErrorType | undefined;
} | {
    type: GaugeSheetStateActionType.DownloadUidChanged;
    payload: string | undefined;
} | {
    type: GaugeSheetStateActionType.ViewModeChanged;
    payload: GaugeSheetViewMode;
} | {
    type: GaugeSheetStateActionType.RefreshingChanged;
    payload: boolean;
} | {
    type: GaugeSheetStateActionType.Reset;
    payload: void;
};

const isDirty = (newState: GaugeSheetStateType, 
    defaultState: GaugeSheetStateType) => {

    const date1 = new Date(newState.date.getFullYear(), newState.date.getMonth());
    const date2 = new Date(defaultState.date.getFullYear(), defaultState.date.getMonth());

    return date1.valueOf() !== date2.valueOf() ||
        newState.showDevice !== defaultState.showDevice ||
        newState.showTanks !== defaultState.showTanks ||
        newState.viewMode !== defaultState.viewMode;
}

function gaugeSheetStateReducer(state: GaugeSheetStateType, action: GaugeSheetStateActions): GaugeSheetStateType {

    switch (action.type) {

        case GaugeSheetStateActionType.ActionsChanged:
            return {...state,
                actions: action.payload //.sort((left, right) => (left.order ?? 0) - (right.order ?? 0))
            };

        case GaugeSheetStateActionType.DateChanged: {

            const copy = {...state,
                previousDate: action.payload,
                date: action.payload
            };

            copy.viewOptionState = isDirty(copy, defaultGaugeSheetState) ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            return copy;
        }

        case GaugeSheetStateActionType.ResetDateChanged:
            return {...state,
                resetDate: action.payload
            };

        case GaugeSheetStateActionType.ValidationErrorChanged: 

            return {...state,
                validationError: action.payload
            };


        case GaugeSheetStateActionType.DataChanged: {

            const data = _.cloneDeep(action.payload);

            const totalRow = data.rows.pop();

            return {...state,
            data: data,
            totalRow: totalRow
            };
        }

        case GaugeSheetStateActionType.ShowDeviceChanged: {
            const copy = {...state,
                showDevice: action.payload,
                showTanks: !action.payload ? true : state.showTanks
            };

            copy.viewOptionState = isDirty(copy, defaultGaugeSheetState) ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;

            return copy;
        }

        case GaugeSheetStateActionType.ShowTankChanged: {
            const copy = {...state,
                showTanks: action.payload,
                showDevice: !action.payload ? true : state.showDevice,
            };

            copy.viewOptionState = isDirty(copy, defaultGaugeSheetState) ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;

            return copy;
        }

        case GaugeSheetStateActionType.GridApiChanged:
            return {
                ...state,
                gridApi: action.payload
            };

        case GaugeSheetStateActionType.TotalRowChanged:
            return {
                ...state,
                totalRow: action.payload
            };
    
        case GaugeSheetStateActionType.DownloadUidChanged:
            return {
                ...state,
                downloadUid: action.payload
            };

        case GaugeSheetStateActionType.ViewModeChanged: {
            const copy = {
                ...state,
                viewMode: action.payload
            };           

            copy.viewOptionState = isDirty(copy, defaultGaugeSheetState) ? ViewOptionStateEnum.Changed : ViewOptionStateEnum.Default;
            return copy;
        }

        case GaugeSheetStateActionType.RefreshingChanged:
            return {...state,
                refreshing: action.payload
            };

        case GaugeSheetStateActionType.Reset:


            return {...defaultGaugeSheetState,
                date: new Date(),
            };
    
        default:
            return state;
    }
}

export default function useGaugeSheetState() {

    // by using a custom hook we could include other state to work on such as app settings state
    // also this simplifies use of dispatch by providing state methods, this would alleviate the 
    // need to use an enum for the action types
    const [state, dispatch] = useReducer(gaugeSheetStateReducer, defaultGaugeSheetState);


    const resetState = useCallback(() => dispatch({
        type: GaugeSheetStateActionType.Reset,
        payload: undefined
    }), []);

    // const setActions = useCallback((value: GaugeSheetActionItem[]) => dispatch({
    //         type: GaugeSheetStateActionType.ActionsChanged,
    //         payload: value
    // }), []);

    const setActions = useCallback((actions: GaugeSheetActionItem[]) => sortAndDispatchActions(
        state.actions,
        actions,
        (sortedActions) => dispatch({
            type: GaugeSheetStateActionType.ActionsChanged,
            payload: sortedActions
        })
    ), [state.actions]);

    const setDate = useCallback((value: Date) => dispatch({
            type: GaugeSheetStateActionType.DateChanged,
            payload: value
    }), []);

    const setData = useCallback((value: GridData) => dispatch({
            type: GaugeSheetStateActionType.DataChanged,
            payload: value
    }), []);

    const setShowDevice = useCallback((value: boolean) => dispatch({
            type: GaugeSheetStateActionType.ShowDeviceChanged,
            payload: value
    }), []);

    const setShowTanks = useCallback((value: boolean) => dispatch({
            type: GaugeSheetStateActionType.ShowTankChanged,
            payload: value
    }), []);

    const setGridApi = useCallback((value: GridApi | undefined) => dispatch({
        type: GaugeSheetStateActionType.GridApiChanged,
        payload: value
    }), []);

    const setValidationError = useCallback((value: GaugeSheetErrorType | undefined) => dispatch({
        type: GaugeSheetStateActionType.ValidationErrorChanged,
        payload: value
    }), []);

    const setTotalRow = useCallback((value: RowDef) => dispatch({
        type: GaugeSheetStateActionType.TotalRowChanged,
        payload: value
    }), []);

    const setDownloadUid = useCallback((value: string | undefined) => dispatch({
        type: GaugeSheetStateActionType.DownloadUidChanged,
        payload: value
    }), []);

    const setViewMode = useCallback((value: GaugeSheetViewMode) => dispatch({
        type: GaugeSheetStateActionType.ViewModeChanged,
        payload: value
    }), []);

    const setResetDate = useCallback((value: boolean) => dispatch({
        type: GaugeSheetStateActionType.ResetDateChanged,
        payload: value
    }), []);

    const setRefreshing = useCallback((value: boolean) => dispatch({
        type: GaugeSheetStateActionType.RefreshingChanged,
        payload: value
    }), []);

    return {
        state,
        setActions,
        setDate,
        setData,
        setShowDevice,
        setShowTanks,
        setGridApi,
        setValidationError,
        setTotalRow,
        setDownloadUid,
        setViewMode,
        setResetDate,
        setRefreshing,
        resetState
    };
}

